import { useState } from 'react'
import { Link } from 'react-router'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowUp, ArrowDown, List, Loader2, RefreshCw, Download, Music } from 'lucide-react'
import { toast } from 'sonner'
import { useApiClient } from '@/shared/api'
import type { InstanceStatus, TaskStatus, StackType } from './InstanceInfoCard'

// Simplified type for serialized instance data
interface SerializedInstance {
  id: number
  status: InstanceStatus
  lastTask: {
    status: TaskStatus
  } | null
}

interface InstanceActionsCardProps {
  instance: SerializedInstance | null
  campaignId: number
  stack: StackType
  onActionComplete?: () => void
}

type ActionType = 'up' | 'down' | 'restart_services' | 'upgrade_services' | 'sync_voices'

export function InstanceActionsCard({
  instance,
  campaignId,
  stack,
  onActionComplete,
}: InstanceActionsCardProps) {
  const apiClient = useApiClient()
  const [isLoading, setIsLoading] = useState<ActionType | null>(null)

  const hasPendingTask =
    instance?.lastTask?.status === 'new' || instance?.lastTask?.status === 'in_progress'

  const handleAction = async (action: ActionType) => {
    if (!instance) return

    setIsLoading(action)
    try {
      const result = await apiClient<{ message: string }>('/dialer/campaign/instance', {
        method: 'POST',
        body: JSON.stringify({
          action,
          campaignId,
          stack,
        }),
      })

      toast.success(result?.message || 'Action completed')
      onActionComplete?.()
    } catch (error: any) {
      toast.error(error?.message || 'An error occurred')
    } finally {
      setIsLoading(null)
    }
  }

  if (!instance) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Actions</CardTitle>
          <CardDescription>Save configuration first to enable actions</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Actions</CardTitle>
        <CardDescription>Manage the {stack} instance</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-3">
        {instance.status === 'down' ? (
          <Button
            onClick={() => handleAction('up')}
            disabled={isLoading !== null || hasPendingTask}
          >
            {isLoading === 'up' ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ArrowUp className="mr-2 h-4 w-4" />
            )}
            Start Instance
          </Button>
        ) : (
          <>
            <Button
              variant="destructive"
              onClick={() => handleAction('down')}
              disabled={isLoading !== null || hasPendingTask}
            >
              {isLoading === 'down' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowDown className="mr-2 h-4 w-4" />
              )}
              Stop Instance
            </Button>

            <Button
              variant="outline"
              onClick={() => handleAction('restart_services')}
              disabled={isLoading !== null || hasPendingTask}
            >
              {isLoading === 'restart_services' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Restart Services
            </Button>

            <Button
              variant="outline"
              onClick={() => handleAction('upgrade_services')}
              disabled={isLoading !== null || hasPendingTask}
            >
              {isLoading === 'upgrade_services' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Upgrade Services
            </Button>

            {stack === 'asterisk' && (
              <Button
                variant="outline"
                onClick={() => handleAction('sync_voices')}
                disabled={isLoading !== null || hasPendingTask}
              >
                {isLoading === 'sync_voices' ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Music className="mr-2 h-4 w-4" />
                )}
                Sync Voices
              </Button>
            )}
          </>
        )}

        <Button variant="outline" asChild>
          <Link to={`/dialer/${campaignId}/admin/${instance.id}/tasks`}>
            <List className="mr-2 h-4 w-4" />
            View Tasks
          </Link>
        </Button>

        {hasPendingTask && (
          <p className="text-body text-amber-600 flex items-center">
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            Task in progress...
          </p>
        )}
      </CardContent>
    </Card>
  )
}